import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useBooking } from "../context/BookingContext";

const ease = [0.25, 1, 0.5, 1];
const headingLines = ["The Art of the", "Masterpiece."];

export const Hero = () => {
  const { openBooking } = useBooking();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      ref={sectionRef}
      id="top"
      data-testid="hero-section"
      className="relative h-screen w-full overflow-hidden flex items-end"
    >
      {/* Background — salon interior hero, subtle scroll parallax */}
      <motion.img
        style={{ y: imgY, scale: imgScale }}
        src="https://customer-assets-gfyr7b9c.emergentagent.net/job_picaso-collections/artifacts/ewfgzu8p_DSC_0043.jpg"
        alt="Picaso Studios salon interior"
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Overlay — stronger on the lower-left to lift the headline */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent" />

      {/* Content - lower left quadrant */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-28 md:pb-36">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.2 }}
          className="text-[#FDFBF7]/80 text-xs uppercase tracking-[0.35em] mb-6"
        >
          Vaughan · Est. 2005
        </motion.p>
        <h1 className="font-serif text-[#FDFBF7] font-light leading-[0.95] text-5xl sm:text-6xl md:text-8xl max-w-4xl">
          {headingLines.map((line, i) => (
            <span key={line} className="block overflow-hidden py-[0.05em]">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, ease, delay: 0.3 + i * 0.15 }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease, delay: 0.55 }}
          className="mt-8 text-[#FDFBF7]/85 font-light text-sm md:text-lg max-w-xl leading-[1.6]"
        >
          Two decades of timeless hair, makeup, and scalp wellness in Vaughan.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease, delay: 0.7 }}
          className="mt-10"
        >
          <button
            data-testid="hero-book-button"
            onClick={() => openBooking()}
            className="group inline-flex items-center gap-3 bg-[#FDFBF7] text-obsidian px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-champagne transition-colors duration-500"
          >
            Secure a Consultation
            <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">
              →
            </span>
          </button>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3">
        <div className="h-14 w-px bg-[#FDFBF7]/30 overflow-hidden">
          <div className="scroll-line h-full w-full bg-[#FDFBF7]" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
